import { routePaths } from './routePaths';

// Roles locked to the Field Mode shell. Only admin keeps the desktop app; every
// other role lands in Field Mode (they can still be reached at /field). pm and qs
// field replacements are now wired, so they join the field-only set.
export const fieldOnlyRoles = [
  'construction',
  'supervisor',
  'worker',
  'construction_worker',
  'accounts',
  'procurement',
  'stores',
  'pm',
  'qs',
  'executive',
  'field_admin',
] as const;

export type FieldOnlyRole = (typeof fieldOnlyRoles)[number];

export const adminFieldViewRoles = [
  'admin',
  'supervisor',
  'worker',
  'procurement',
  'stores',
  'accounts',
  'qs',
  'pm',
  'executive',
] as const;

export type AdminFieldViewRole = (typeof adminFieldViewRoles)[number];

export const normalizeRole = (role?: string | null): string =>
  (role || '').trim().toLowerCase();

export const isFieldOnlyRole = (role?: string | null): role is FieldOnlyRole =>
  fieldOnlyRoles.includes(normalizeRole(role) as FieldOnlyRole);

export const fieldLandingForRole = (role?: string | null): string =>
  isFieldOnlyRole(role) ? '/field' : '/dashboard';

export const changePasswordPathForRole = (role?: string | null): string =>
  isFieldOnlyRole(role) ? routePaths.fieldChangePassword : routePaths.changePassword;

export const isFieldPath = (pathname: string): boolean =>
  pathname === '/field' || pathname.startsWith('/field/');

export const isAdminFieldViewRole = (role?: string | null): role is AdminFieldViewRole =>
  adminFieldViewRoles.includes(normalizeRole(role) as AdminFieldViewRole);

export const canUseAdminFieldViewSwitcher = (role?: string | null): boolean =>
  ['admin', 'field_admin'].includes(normalizeRole(role));

export const canUseAdminFieldViewSwitcherForUser = (role?: string | null, _email?: string | null): boolean =>
  canUseAdminFieldViewSwitcher(role);

export const fieldViewFromPath = (pathname: string): AdminFieldViewRole | null => {
  if (pathname.startsWith('/field/admin')) return 'admin';
  if (pathname.startsWith('/field/notifications')) return null;
  if (pathname.startsWith('/field/accounts')) return 'accounts';
  if (pathname.startsWith('/field/procurement')) return 'procurement';
  if (pathname.startsWith('/field/stores')) return 'stores';
  if (pathname.startsWith('/field/qs')) return 'qs';
  if (pathname.startsWith('/field/pm')) return 'pm';
  if (pathname.startsWith('/field/executive')) return 'executive';
  if (
    pathname.startsWith('/field/tasks') ||
    pathname.startsWith('/field/upcoming') ||
    pathname.startsWith('/field/performance') ||
    pathname.startsWith('/field/worker') ||
    pathname.startsWith('/field/workers') ||
    pathname.startsWith('/field/attendance') ||
    pathname.startsWith('/field/qc-templates') ||
    pathname.startsWith('/field/qc-runs')
  ) {
    return 'worker';
  }
  if (
    pathname.startsWith('/field/inspections') ||
    pathname.startsWith('/field/dfr') ||
    pathname.startsWith('/field/dispatch') ||
    pathname.startsWith('/field/timesheets') ||
    pathname.startsWith('/field/defects') ||
    pathname.startsWith('/field/emergency')
  ) {
    return 'supervisor';
  }
  return null;
};

export const effectiveFieldRoleForUser = (
  userRole?: string | null,
  requestedView?: string | null,
  pathname = '/field',
  userEmail?: string | null
): string => {
  const normalizedRole = normalizeRole(userRole);
  if (canUseAdminFieldViewSwitcherForUser(normalizedRole, userEmail)) {
    if (isAdminFieldViewRole(requestedView)) return normalizeRole(requestedView);
    return fieldViewFromPath(pathname) ?? 'admin';
  }
  return normalizedRole;
};
